import { useState, MouseEvent } from "react";

import {
  IconButton,
  Typography,
  Tooltip,
  Badge,
  Menu,
  Box,
  Chip,
  Divider,
  Grow,
} from "@mui/material";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";
import { ComponentPrivate } from "src/Pages/components/ComponentPrivate/ComponentPrivate";
import { LinkCore } from "src/Pages/components/LinkCore/LinkCore";

import moment from "moment";
import { listUpdates } from "./listUpdates";
import { AccessTimeIcon, ArrowForwardIcon, MailIcon, NewReleasesIcon } from "src/Pages/components/Icons/Icons";

export const MenuNovidade = () => {
  const { userPerfil } = useAuthContext();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const isNotViewProducer = userPerfil?.type_user === "PRODUCER";

  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const hasNovidade = listUpdates.some((item) => {
    if (!item.limitNovidade) return false;
    return moment().isBefore(moment(item.limitNovidade));
  });

  const getNovidadeCount = () => {
    return listUpdates.filter((item) => {
      if (!item.limitNovidade) return false;
      return moment().isBefore(moment(item.limitNovidade));
    }).length;
  };

  const isItemActive = (limitNovidade: string) => {
    return moment().isBefore(moment(limitNovidade));
  };

  return (
    <>
      <Tooltip title="Ver novidades">
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{
            position: "relative",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              transform: "scale(1.05)",
              transition: "all 0.2s ease-in-out",
            },
          }}
        >
          <Badge
            badgeContent={getNovidadeCount()}
            color={hasNovidade ? "warning" : "primary"}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                height: "20px",
                minWidth: "20px",
                borderRadius: "10px",
                fontWeight: "bold",
              },
            }}
          >
            <MailIcon
              color="action"
              sx={{
                fontSize: "24px",
                transition: "all 0.2s ease-in-out",
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        keepMounted
        PaperProps={{
          style: {
            width: "420px",
            maxHeight: "600px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            padding: "0px",
          },
        }}
        anchorEl={anchorElUser}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={Boolean(anchorElUser)}
        sx={{
          mt: "8px",
          padding: "0px",
          "& .MuiList-root": {
            padding: 0,
          },
          "& .MuiMenu-paper": {
            overflow: "visible",
          },
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseUserMenu}
        TransitionComponent={Grow}
        transitionDuration={200}
      >
        <ComponentPrivate isView={!isNotViewProducer}>
          <Box sx={{ p: 0 }}>
            {/* Header do Menu */}
            <Box
              sx={{
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: "white",
                p: 1.5,
                borderRadius: "12px 12px 0 0",
                position: "relative",
                overflow: "hidden",
                m: 0,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.3,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <NewReleasesIcon sx={{ mr: 1, fontSize: "24px" }} />
                <Typography variant="h6">
                  Novidades
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {getNovidadeCount()} novidade
                {getNovidadeCount() !== 1 ? "s" : ""} disponíve
                {getNovidadeCount() !== 1 ? "is" : "l"}
              </Typography>
            </Box>

            {/* Lista de Novidades */}
            <Box sx={{ p: 2, maxHeight: "400px", overflowY: "auto" }}>
              {listUpdates.map((item, index) => (
                <LinkCore to={item.url} key={index}>
                  <Tooltip title="Clique para ver a novidade">
                    <Box
                      sx={{
                        border: `2px solid ${isItemActive(item.limitNovidade)
                          ? "rgba(25, 118, 210, 0.2)"
                          : "rgba(0, 0, 0, 0.08)"
                          }`,
                        background: isItemActive(item.limitNovidade)
                          ? "rgba(25, 118, 210, 0.02)"
                          : "rgba(0, 0, 0, 0.02)",
                        padding: "16px",
                        borderRadius: "12px",
                        mb: "12px",
                        cursor: "pointer",
                        transition: "all 0.3s ease-in-out",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
                          borderColor: (theme) =>
                            isItemActive(item.limitNovidade)
                              ? theme.palette.primary.main
                              : "rgba(0, 0, 0, 0.2)",
                          "& .novidade-arrow": {
                            transform: "translateX(4px)",
                            opacity: 1,
                          },
                          "& .novidade-status": {
                            transform: "scale(1.1)",
                          },
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "4px",
                          height: "100%",
                          background: (theme) =>
                            isItemActive(item.limitNovidade)
                              ? theme.palette.primary.main
                              : "rgba(0, 0, 0, 0.3)",
                          borderRadius: "0 2px 2px 0",
                        },
                      }}
                    >
                      {/* Status Badge */}
                      <Box
                        className="novidade-status"
                        sx={{
                          position: "absolute",
                          top: "12px",
                          right: "12px",
                          transition: "all 0.3s ease-in-out",
                        }}
                      >
                        <Chip
                          icon={
                            isItemActive(item.limitNovidade) ? (
                              <NewReleasesIcon sx={{ fontSize: "16px" }} />
                            ) : (
                              <AccessTimeIcon sx={{ fontSize: "16px" }} />
                            )
                          }
                          label={
                            isItemActive(item.limitNovidade)
                              ? "Nova"
                              : "Recente"
                          }
                          size="small"
                          color={
                            isItemActive(item.limitNovidade)
                              ? "primary"
                              : "default"
                          }
                          variant={
                            isItemActive(item.limitNovidade)
                              ? "filled"
                              : "outlined"
                          }
                          sx={{
                            fontSize: "0.7rem",
                            height: "24px",
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      {/* Conteúdo Principal */}
                      <Box sx={{ pr: "80px" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            color: (theme) =>
                              isItemActive(item.limitNovidade)
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                            mb: 1,
                            lineHeight: 1.3,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Box sx={{ mb: 2 }}>{item.description}</Box>

                        {/* Footer com Data */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: (theme) =>
                                isItemActive(item.limitNovidade)
                                  ? theme.palette.primary.main
                                  : theme.palette.text.disabled,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: "14px" }} />
                            {item.createdAt}
                          </Typography>

                          <ArrowForwardIcon
                            className="novidade-arrow"
                            sx={{
                              fontSize: "18px",
                              color: (theme) =>
                                isItemActive(item.limitNovidade)
                                  ? theme.palette.primary.main
                                  : theme.palette.text.disabled,
                              opacity: 0.7,
                              transition: "all 0.3s ease-in-out",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Tooltip>
                </LinkCore>
              ))}
            </Box>

            {/* Footer do Menu */}
            <Divider sx={{ mx: 2 }} />
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                background: "rgba(0, 0, 0, 0.02)",
                borderRadius: "0 0 16px 16px",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "11px",
                  opacity: 0.7,
                }}
              >
                Clique em qualquer novidade para acessar
              </Typography>
            </Box>
          </Box>
        </ComponentPrivate>
      </Menu>
    </>
  );
};
